/**
 * @fileoverview Custom hook for enrollment-related Pendo tracking.
 * Simplifies tracking by encapsulating common context and patterns.
 */

import { useSelector } from "react-redux";
import { selectApplicationForm, selectCurrentApplication } from "../../../../../store/selector";
import { useStandardizedTracking, EventNames, FeatureGroups } from "../../../../../utils/pendo/useStandardizedTracking";
import { getCurrentStep } from "../utils/getCurrentStep";

/**
 * Custom hook for enrollment tracking.
 * Provides helper functions with pre-filled context for common tracking scenarios.
 *
 * @returns {Object} Tracking helper functions and context.
 *
 * @example
 * const { trackButtonClick, trackPopupImpression, currentStep, totalSteps } = useEnrollmentTracking();
 *
 * trackButtonClick("edit_form_icon_button", {
 *   button_id: "EE_edit_application_form",
 *   button_text: "Edit"
 * });
 */
export const useEnrollmentTracking = () => {
  const { track } = useStandardizedTracking();
  const applicationForm = useSelector(selectApplicationForm);
  const application = useSelector(selectCurrentApplication);

  const potentialSaving = application?.potentialSaving;
  const templateName = potentialSaving?.templateName;
  const applicationFormId = applicationForm?.id;
  const assistanceProgram = potentialSaving?.assistanceProgram;
  const isExpressEnrollment = !!application?.hasExpressEnroll;
  const currentStep = getCurrentStep(applicationForm);
  const applicationFormStatus = applicationForm?.status;

  /**
   * Get the appropriate feature group based on enrollment type.
   *
   * @returns {string} Feature group constant.
   */
  const getFeatureGroup = () => (isExpressEnrollment ? FeatureGroups.EXPRESS_ENROLLMENT : FeatureGroups.ENROLLMENTS);

  /**
   * Track a button click event.
   *
   * @param {string} contentKey - The content key for the button (e.g., "edit_form_icon_button").
   * @param {Object} additionalInfo - Additional info to include in the event.
   * @param {string|Object} [contentTypeOrOptions="form"] - The content type string or options object.
   */
  const trackButtonClick = (contentKey, additionalInfo = {}, contentTypeOrOptions = "form") => {
    const isOptionsObject = typeof contentTypeOrOptions === "object";
    const contentType = isOptionsObject ? contentTypeOrOptions.contentType || "form" : contentTypeOrOptions;
    const feature = isOptionsObject ? contentTypeOrOptions.feature : undefined;

    track(EventNames.BUTTON_CLICK, {
      featureGroup: getFeatureGroup(),
      feature,
      pageKey: "enrollment_details",
      contentType,
      contentKey,
      potentialSaving,
      assistanceProgram,
      additionalInfo: {
        is_express_enrollment: isExpressEnrollment,
        current_step: currentStep,
        template_name: templateName,
        application_form_id: applicationFormId,
        ...additionalInfo
      }
    });
  };

  /**
   * Track an icon button click event.
   *
   * @param {string} contentKey - The content key for the icon button.
   * @param {Object} additionalInfo - Additional info to include in the event.
   */
  const trackIconClick = (contentKey, additionalInfo = {}) => {
    trackButtonClick(contentKey, additionalInfo, "Icon");
  };

  /**
   * Track a popup impression event.
   *
   * @param {string} contentKey - The content key for the popup.
   * @param {Object} additionalInfo - Additional info to include in the event.
   */
  const trackPopupImpression = (contentKey, additionalInfo = {}) => {
    track(EventNames.POP_UP_IMPRESSION, {
      featureGroup: getFeatureGroup(),
      pageKey: "enrollment_details",
      contentType: "form",
      contentKey,
      layout: "popup",
      potentialSaving,
      assistanceProgram,
      additionalInfo: {
        is_express_enrollment: isExpressEnrollment,
        current_step: currentStep,
        application_form_status: applicationFormStatus,
        template_name: templateName,
        application_form_id: applicationFormId,
        ...additionalInfo
      }
    });
  };

  /**
   * Track a box/section impression event (e.g., enroll tab content).
   *
   * @param {string} contentKey - The content key for the box.
   * @param {Object} additionalInfo - Additional info to include in the event.
   * @param {Object} options - Required options for feature, contentType, layout.
   * @param {string} options.feature - Feature identifier (e.g., "ee_first_step", "smart_sign", "docusign", "ee_third_step").
   * @param {string} options.contentType - Content type (e.g., "Application", "widget").
   * @param {string} options.layout - Layout type (e.g., "tab_content", "step", "widget", "form").
   */
  const trackBoxImpression = (contentKey, additionalInfo = {}, options = {}) => {
    const { feature, contentType, layout } = options;
    track(EventNames.BOX_IMPRESSION, {
      featureGroup: getFeatureGroup(),
      feature,
      pageKey: "enrollment_details",
      contentType,
      contentKey,
      layout,
      potentialSaving,
      assistanceProgram,
      additionalInfo: {
        current_step: currentStep,
        supports_express_enrollment: isExpressEnrollment,
        template_name: templateName,
        application_form_id: applicationFormId,
        application_form_status: applicationFormStatus,
        ...additionalInfo
      }
    });
  };

  /**
   * Track a link click event.
   *
   * @param {string} contentKey - The content key for the link.
   * @param {Object} additionalInfo - Additional info to include in the event.
   * @param {Object} [options] - Optional overrides for feature.
   */
  const trackLinkClick = (contentKey, additionalInfo = {}, options = {}) => {
    const { feature } = options;
    track(EventNames.LINK_CLICK, {
      featureGroup: getFeatureGroup(),
      feature,
      pageKey: "enrollment_details",
      contentType: "Link",
      contentKey,
      potentialSaving,
      assistanceProgram,
      additionalInfo: {
        is_express_enrollment: isExpressEnrollment,
        current_step: currentStep,
        template_name: templateName,
        application_form_id: applicationFormId,
        ...additionalInfo
      }
    });
  };

  return {
    trackButtonClick,
    trackIconClick,
    trackPopupImpression,
    trackBoxImpression,
    trackLinkClick,
    application
  };
};

export default useEnrollmentTracking;
